import { createContext, useContext } from "react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import utc from "dayjs/plugin/utc";

import { convertDbEmissionsToTransactions } from "@aclymatepackages/other-helpers";
import { buildCompanyAggregatedEmissionsArrays } from "@aclymatepackages/calcs/recurring";
import { subcategories } from "@aclymatepackages/subcategories";

import { useEmployeeStatuses } from "../components/employees";
import {
  groupRollupsV2VendorDocsByVendor,
  shouldUseRollupsV2Read
} from "../components/rollupsV2";
import usePlatformLayoutContext from "./platformLayout";
import { AdminDataContext } from "./adminDataContext";

import {
  useCachedDisplayData,
  useAccountData,
  useCollectionDataListener
} from "../firebase";
import { useDisplayEvents } from "../components/events";
import { useTransactionsMonthsData } from "../components/emissions";
import { useFindCurrentEmployeeEmissions } from "../components/employees";
import { formatVendors } from "../components/vendors";
import useAccountingData from "../hooks/accountingData";
import { readElectricityMonthSnapshot } from "../utils/accountingElectricitySnapshot";

dayjs.extend(isSameOrAfter);
// calculateCurrentCarbonBalanceTons calls isSameOrBefore, which until now
// only worked because helpers/components/emissions extends it on the shared
// dayjs singleton and this file happens to import that module. Extending it
// here too so dropping that import can't turn the balance calc into a
// runtime TypeError.
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);

// Electricity fields for one snapshot month's electricity entry. With the
// location snapshot present, the entry carries renewableCarbonTonsCo2e (plus
// kWh and RECs kWh), so Report.js's calculateElectricityLocationAndMarketTons
// derives Location exactly as it does for an open year. A snapshot written
// before those fields existed cannot distinguish "no RECs" from "RECs we
// never recorded", so its electricity is flagged locationBasedUnavailable
// and the report says so instead of printing Market as Location. The same
// flag covers a month the close recorded as unknown (null Location: RECs kWh
// with no way to price them).
const buildSnapshotElectricityFields = (accountingYear, monthIndex) => {
  const electricityMonth = readElectricityMonthSnapshot(
    accountingYear,
    monthIndex
  );

  if (!electricityMonth) {
    return { locationBasedUnavailable: true };
  }

  const { recsTons, kwh, recsKwh } = electricityMonth;
  return {
    renewableCarbonTonsCo2e: recsTons,
    electricKwh: kwh,
    recsKwh
  };
};

// A month whose electricity was fully REC-covered still has a snapshot
// "electricity" key (0 tons), but guard the case where the location arrays
// record electricity activity for a month with no such key (e.g. a backfill
// found RECs the close-time snapshot rolled up elsewhere): emit a zero-ton
// Market entry so the Location figure is not silently dropped. Zero tons,
// so it never changes any total.
const hasElectricityActivity = (accountingYear, monthIndex) => {
  const electricityMonth = readElectricityMonthSnapshot(
    accountingYear,
    monthIndex
  );
  return (
    !!electricityMonth &&
    (electricityMonth.recsTons > 0 ||
      electricityMonth.locationTons > 0 ||
      electricityMonth.kwh > 0)
  );
};

const withElectricityKey = (subcategoriesObj, accountingYear, monthIndex) =>
  "electricity" in subcategoriesObj ||
  !hasElectricityActivity(accountingYear, monthIndex)
    ? subcategoriesObj
    : { ...subcategoriesObj, electricity: 0 };

export const convertAccountingYearsToEmissions = (accountingYears = []) =>
  accountingYears.flatMap((accountingYear) => {
    const { year, monthlySubcategoryEmissionsTons = [] } = accountingYear;
    return monthlySubcategoryEmissionsTons.flatMap((subcategoriesObj, monthIndex) => {
      // Bare dayjs(), deliberately -- do NOT "fix" this to dayjs.utc() to
      // match the closedYears filter below. That filter READS instants other
      // systems already anchored to UTC; this line CONSTRUCTS a date for
      // consumers that all read it back with bare dayjs(): chart-helpers'
      // groupEmissionsByChartLabelsArray, explorer dashboardHelpers'
      // filterEmissionsForYear, PurchaseVolumeSelectionInput. A UTC midnight
      // month start reads back as the PREVIOUS month (January: the previous
      // year) in every negative-UTC-offset timezone, which files a closed
      // 2024's January under Q4 2023 on the dashboard -- the same
      // mislabeling this file's closedYears fix exists to remove.
      const date = dayjs()
        .year(year)
        .month(monthIndex)
        .startOf("month")
        .toDate();

      return Object.entries(
        withElectricityKey(subcategoriesObj || {}, accountingYear, monthIndex)
      ).map(([subcategory, tonsCo2e]) => {
        const { scope } =
          subcategories.find(
            (subcategoryObj) => subcategoryObj.subcategory === subcategory
          ) || {};

        return {
          date,
          scope,
          subcategory,
          tonsCo2e,
          source: "accounting",
          ...(subcategory === "electricity"
            ? buildSnapshotElectricityFields(accountingYear, monthIndex)
            : {})
        };
      });
    });
  });

// Bare dayjs(), not dayjs.utc() -- this used to read UTC deliberately (see
// git history), which fixed the Mountain-Time-observed bug (Gordon Sinclair,
// Shelbyville Pencil Company; same class as the Bag Makers fix, PRs
// #2027/#2029) but broke the opposite direction for every other entry type
// mixed into `entries`, which is NOT uniformly UTC-anchored:
//
// - Recurring emissions (calcs/recurring) are anchored to LOCAL month
//   starts (new Date(year, month, 1)). They're computed fresh by whichever
//   browser is viewing the dashboard and read by this SAME function in the
//   SAME render -- construction and read always share one ambient
//   timezone, so a bare local read is trivially self-consistent in every
//   timezone. A UTC read is the one that's wrong here: for any
//   POSITIVE-UTC-offset viewer (confirmed live: 93 production companies
//   have an office in one -- UAE, Turkey, Poland, India, and more), local
//   midnight on January 1st is UTC-parsed as December 31st of the PREVIOUS
//   year, so a closed year's January recurring entry read as the prior
//   year, escaped closedYears.has(...), and got double-counted the same
//   way the Mountain-Time bug did, just mirrored onto the other side of
//   UTC. Zero customers had hit this yet (none had locked a year at time of
//   writing) but the precondition -- a real customer, viewing from a
//   positive offset -- already existed on 93 accounts.
// - Transaction/rollup months (legacy's correctDateForAggregation, and
//   reshapeRollupsV2MonthDocsToLegacyShape since #2091) are UTC-anchored
//   WITH a deliberate +1-day margin specifically so a bare LOCAL read still
//   lands on the correct month for any real-world UTC offset (-12 to +14):
//   push at most 14h forward from day-2-UTC-midnight never leaves day 2;
//   pull at most 12h back never leaves day 1. That margin exists
//   specifically to make this a safe read, not an unsafe one -- see
//   reshapeRollupsV2MonthDocsToLegacyShape's own comment.
// - Manually-entered emissions carry a real local timestamp the user
//   typed in. A bare local read of that is exactly correct, in any
//   timezone; a UTC read is wrong whenever the real local time is near a
//   month/day boundary UTC doesn't share -- e.g. an entry logged at 11pm
//   Dec 31 in Mountain Time was ALREADY misread as the following year
//   under the UTC read this replaces, independent of the positive/negative
//   offset question above. See this function's tests for a reproduction.
export const filterEntriesOutsideClosedYears = (entries, closedYears) =>
  entries.filter(
    ({ date, emissionDate }) => !closedYears.has(dayjs(emissionDate || date).year())
  );

const EmissionsContext = createContext();

export const calculateCurrentCarbonBalanceTons = ({
  allEmissions,
  offsets = []
}) => {
  const offsetsTonsSum = offsets.length
    ? offsets.reduce(
        (sum, { tonsCo2e = 0 }) => Number(sum) + Number(tonsCo2e),
        0
      )
    : 0;

  const sumTransactions = () => {
    if (!allEmissions.length) {
      return 0;
    }

    // Local on both sides, deliberately -- allEmissions mixes UTC-anchored
    // entries with local-anchored recurring ones (calcs/recurring builds
    // month starts via new Date(year, month, 1)), so a UTC day comparison
    // pulls NEXT month's recurring emissions into the balance from 18:00
    // local on the last day of each month in Mountain Time: their local
    // midnight already shares a UTC day with dayjs.utc().
    const transactionsBeforeToday = allEmissions.filter(
      ({ emissionDate, date }) =>
        dayjs(emissionDate || date).isSameOrBefore(dayjs(), "day")
    );

    return transactionsBeforeToday.reduce(
      (sum, { tonsCo2e }) => sum + (tonsCo2e ? Number(tonsCo2e) : 0),
      0
    );
  };

  const emissionsSum = sumTransactions();
  const currentCarbonBalanceTons = offsetsTonsSum - emissionsSum;

  return { emissionsSum, offsetsTonsSum, currentCarbonBalanceTons };
};

export const EmissionsContextProvider = ({ children }) => {
  const { viewMode = "company" } = usePlatformLayoutContext() || {};
  const { adminData } = useContext(AdminDataContext) || {};

  const { companyData: adminCompanyData } = adminData || {};
  const { startDate: adminStartDate, employeeCount: adminEmployeeCount } =
    adminCompanyData || {};

  const [
    { earliestNewEmissionDate, accountingYears: closedAccountingYears = [] },
    accountingDateLoading
  ] = useAccountingData();
  const [offsets, offsetsLoading] = useCachedDisplayData("offsets");
  const [accountData, accountDataLoading] = useAccountData();
  const { startDate: companyStartDate, employeeCount: companyEmployeeCount } =
    accountData;

  const [utilitiesTransactions, utilitiesTransactionsLoading] =
    useCachedDisplayData("transactions", [
      ["subcategory", "in", ["gas", "electricity", "utilities"]]
    ]);
  const [dbEmissions, dbEmissionsLoading] = useCachedDisplayData("emissions");
  const [dbEmployees, dbEmployeesLoading] = useEmployeeStatuses();
  const [offices, officesLoading] = useCachedDisplayData("offices");
  const [events, eventsLoading] = useDisplayEvents();
  const [transactionsMonthsData, transactionsMonthsDataLoading] =
    useTransactionsMonthsData();

  // Phase 3 read-cutover (per-company canary): same rollupsV2ReadEnabled
  // flag useTransactionsMonthsData checks -- see that hook's own comment,
  // including why admin mode is excluded regardless of the flag (admin
  // support for rollups-v2 is a real follow-up, not built here).
  // rollups-v2-vendors is one doc per vendor PER MONTH (map-based
  // subcategories), so it's grouped back into the per-vendor
  // {companyId, aggregatedTransactions} shape formatVendors already expects
  // before this value ever reaches it.
  const { rollupsV2ReadEnabled } = accountData;
  const effectiveRollupsV2ReadEnabled = shouldUseRollupsV2Read(
    rollupsV2ReadEnabled,
    viewMode
  );
  // Same monthKey>=startDate boundary useTransactionsMonthsData already
  // applies to the company-level rollups-v2 collection -- nothing upstream
  // (write-time rollups, backfill-rollups-v2.js) excludes a transaction
  // just for predating a company's configured startDate, so without this
  // filter a vendor's pre-startDate history (real, historical, but out of
  // this company's reporting scope by design) would show up here even
  // though it's correctly absent from the company-level total. Legacy's
  // matched-vendors-aggregated-transactions needs no equivalent filter --
  // it's populated by the aggregation cron's own date>=startDate query, so
  // it never contains pre-startDate contributions to begin with.
  // No companyStartDate yet means no boundary to apply -- see the identical
  // guard + rationale in useTransactionsMonthsData (emissions.js), which
  // this filter deliberately mirrors.
  const vendorRollupsQueries =
    effectiveRollupsV2ReadEnabled && companyStartDate
      ? [["monthKey", ">=", dayjs.utc(companyStartDate).format("YYYY-MM")]]
      : [];
  const [rawVendorRollups, matchedVendorTransactionsLoading] =
    useCollectionDataListener(
      effectiveRollupsV2ReadEnabled
        ? "rollups-v2-vendors"
        : "matched-vendors-aggregated-transactions",
      vendorRollupsQueries
    );
  const matchedVendorTransactions = effectiveRollupsV2ReadEnabled
    ? groupRollupsV2VendorDocsByVendor(rawVendorRollups)
    : rawVendorRollups;
  const [vendorsWithRelationships, vendorsWithRelationshipsLoading] =
    useCachedDisplayData("vendor-relationships");

  const [findCurrentEmployeeEmissions, employeeTransactionsLoading] =
    useFindCurrentEmployeeEmissions();

  const recurringEmissionsLoading =
    accountingDateLoading ||
    offsetsLoading ||
    dbEmployeesLoading ||
    officesLoading ||
    accountDataLoading ||
    utilitiesTransactionsLoading ||
    eventsLoading ||
    dbEmissionsLoading ||
    transactionsMonthsDataLoading;

  const employeesLoading =
    recurringEmissionsLoading ||
    accountDataLoading ||
    dbEmployeesLoading ||
    employeeTransactionsLoading;

  const vendorsLoading =
    vendorsWithRelationshipsLoading ||
    recurringEmissionsLoading ||
    matchedVendorTransactionsLoading;

  const emissionStartDates = {
    admin: adminStartDate,
    company: earliestNewEmissionDate || companyStartDate,
    simulation: companyStartDate
  };

  const employeeCount =
    viewMode === "admin" ? adminEmployeeCount : companyEmployeeCount;

  const emissionsStartDate = emissionStartDates[viewMode];

  const buildEmissionsArraysFromExternalData = () => {
    if (recurringEmissionsLoading) {
      return {};
    }

    const { allEmissions, ...otherEmissionsArrays } =
      buildCompanyAggregatedEmissionsArrays({
        companyData: {
          ...accountData,
          startDate: emissionsStartDate
        },
        dbEmissions,
        employees: dbEmployees,
        offices,
        events,
        utilitiesTransactions
      });

    const dbEmissionsAsTransactions =
      convertDbEmissionsToTransactions(dbEmissions);

    const combinedEmissions = [
      ...allEmissions,
      ...dbEmissionsAsTransactions,
      ...transactionsMonthsData
    ];

    const closedYears = new Set(closedAccountingYears.map(({ year }) => year));

    const hasClosedYears = closedYears.size > 0;

    const emissionsWithAccountingData = hasClosedYears
      ? [
          ...filterEntriesOutsideClosedYears(combinedEmissions, closedYears),
          ...convertAccountingYearsToEmissions(closedAccountingYears)
        ]
      : combinedEmissions;

    return {
      ...otherEmissionsArrays,
      allEmissions: emissionsWithAccountingData
    };
  };

  const {
    emissionsMonths = [],
    monthlyCommuteEmissionsTons = [],
    monthlyUtilitiesEmissionsTons: rawMonthlyUtilitiesEmissionsTons = [],
    separateUtilitiesOfficesDefaultEmissions = [],
    allEmissions = []
  } = buildEmissionsArraysFromExternalData();

  const monthlyUtilitiesEmissionsTons =
    rawMonthlyUtilitiesEmissionsTons.flatMap((item) =>
      Array.isArray(item) ? item : [item]
    );

  const filteredOffsets = offsets?.length
    ? offsets.filter(({ status }) => status !== "failed")
    : [];

  const { currentCarbonBalanceTons, emissionsSum } =
    calculateCurrentCarbonBalanceTons({
      allEmissions,
      offsets: filteredOffsets
    });

  const formatDbEmployees = () => {
    if (employeesLoading) {
      return [];
    }

    return findCurrentEmployeeEmissions({
      employees: dbEmployees,
      employeeCount,
      emissionsMonths
    });
  };

  const formatDbVendors = () => {
    if (vendorsLoading) {
      return [];
    }

    return formatVendors({
      vendorsWithRelationships,
      matchedVendorTransactions,
      transactionsMonthsData,
      emissionsSum
    });
  };

  const employees = formatDbEmployees();

  const vendors = formatDbVendors();

  return (
    <EmissionsContext.Provider
      value={{
        emissionsMonths,
        monthlyCommuteEmissionsTons,
        monthlyUtilitiesEmissionsTons,
        emissionsLoading: recurringEmissionsLoading,
        separateUtilitiesOfficesDefaultEmissions,
        allEmissions,
        utilitiesTransactions,
        employees,
        vendors,
        currentCarbonBalanceTons
      }}
    >
      {children}
    </EmissionsContext.Provider>
  );
};

const useEmissionsContext = () => useContext(EmissionsContext);
export default useEmissionsContext;
